'use strict';

import {newUTCDate, addMonthsMaintainDate} from "./generic/calendarCalculator";
// imports for typing
import {Transaction} from "../captable/transaction";
import {CapTable} from "../captable/captable";
import {LedgerEntry} from "../captable/ledgerentry";
import * as moment from "moment";

export function currentDate() {
    // returns the current local date for the user, at midnight, with no (or at UTC) timezone
    let now = new Date();
    return newUTCDate(now.getFullYear(), now.getMonth(), now.getDate());
}

export function daysBetween(start:Date, ended:Date) {
    if (!start || !ended || typeof start == 'string' || typeof ended == 'string') {
        return 0;
    }
    const t1 = Math.floor(start.getTime() / 86400000);
    const t2 = Math.floor(ended.getTime() / 86400000);
    return t2 - t1;
}

export function minDate(prev:Date, cur:Transaction) {
    if (cur.effective_date && (!prev || cur.effective_date < prev)) {
        return cur.effective_date;
    }
    return prev;
}

export function vestingFrequency(freq:frequency_string) {
    switch (freq) {
        case 'monthly':
            return 1;
        case 'weekly':
            return 0.25;
        case 'bi-weekly':
            return 0.5;
        case 'quarterly':
            return 3;
        case 'yearly':
        case 'yearly simple':
            return 12;
        default:
            return undefined;
    }
}

// TODO MAYBE Technically should look at whether it's a leap year.
// this is such a tiny, tiny difference it's probably not worth doing however.
export function effectivePeriodInterest(_interestRate:number, rateFreq:frequency_string, count:string) {
    let interestRate = parseFloat(String(_interestRate));
    if (isNaN(interestRate)) {
        return 0;
    }
    let rate = 0;
    if (rateFreq == 'weekly') {
        rate = interestRate / (365 / 7);
    } else if (rateFreq == 'bi-weekly') {
        rate = interestRate / (365 / 14);
    // Only the below support 360 day math
    } else if (rateFreq == 'monthly') {
        rate = interestRate / 12;
    } else if (rateFreq == 'quarterly') {
        rate = interestRate / 4;
    } else if (rateFreq.includes('yearly')) {
        rate = interestRate;
    }
    if (count == 'Actual/360') {
        rate = rate * (365/360);
    }
    return rate / 100;
}

export function dayFraction(start:moment.Moment, end:moment.Moment, dayCount:string, days:number) {
    // This function calculates the day fraction on the basis that the differences between start and end is less than a year
    if (end.diff(start, 'years') !== 0) {
        return 0;
    }
    let fraction = 0;
    if (dayCount == "30/360") {
        fraction += days / 360;
    } else if (dayCount == "Actual/365 Fixed") {
        fraction += days / 365;
    } else {
        if (start.year() == end.year()) {
            if (start.isLeapYear()) {
                fraction += days / 366;
            } else {
                fraction += days / 365;
            }
        } else {
            let newyear = moment.utc([end.year(),1 ,1]);
            let yearone = end.diff(newyear, 'days');
            let yeartwo = newyear.diff(start, 'days');
            if (start.isLeapYear()) {
                fraction += yearone / 366;
            } else {
                fraction += yearone / 365;
            }
            if (end.isLeapYear()) {
                fraction += yeartwo / 366;
            } else {
                fraction += yeartwo / 365;
            }
        }
    }
    return fraction;
}

export function interestAdded(amount:number, startAmount:number, rate:number, compoundingFrequency:string, periods = 1, fraction = 1) {
    if (compoundingFrequency.includes("simple")) {
        return amount + (startAmount * rate * fraction * periods);
    } else {
        return amount * Math.pow((1 + (rate * fraction)), periods);
    }
}

function calcDiffs(start:moment.Moment, end:moment.Moment) {
    return {
        days: end.diff(start, 'days'),
        months: end.diff(start, 'months'),
        years: end.diff(start, 'years')
    };
}

// Using 30/360 US Bond definition
// See 4.16f of http://www.hsbcnet.com/gbm/attachments/standalone/2006-isda-definitions.pdf
export function dayDiff30_360_Bond(start:moment.Moment, end:moment.Moment) {
    let startday = Math.min(start.date(), 30);
    let endday = end.date();
    if (startday == 30) {
        endday = Math.min(end.date(), 30);
    }
    if (endday == 31 && startday == 30) {
        endday = 30;
    }
    if (start.month() == end.month()) {
        return endday - startday;
    }
    return  endday + (30 - startday);
}

// Defaults used to maintain historic behavior, may wish to revist in future
export function interestCalc(startAmount:number, startDate:Date, endDate:Date, interestrate:number, compoundingFrequency:frequency_string = 'yearly simple', dayCount = "Actual/365") {
    let start = moment.utc(startDate);
    let end = moment.utc(endDate);
    // Track the initial start date to work around momentJS quirk in adding months.
    let startDay = start.date();
    // Track starting amount for non-compounding interest
    let amount = startAmount;
    let rate = effectivePeriodInterest(interestrate, compoundingFrequency, dayCount);
    let diffs = calcDiffs(start, end);
    let frequency = vestingFrequency(compoundingFrequency);
    if (frequency === undefined) {
        return amount;
    }
    // Annual Interest
    if (frequency == 12) {
        if (diffs.years > 0) {
            amount = interestAdded(amount, startAmount, rate, compoundingFrequency, diffs.years);
            start.add(diffs.years, "years");
            diffs = calcDiffs(start, end);
        }
        // Calulate the remaining days
        if (diffs.days > 0) {
            let fraction = 0;
            // If 30 day count months separately
            if (diffs.months > 0 && dayCount.includes("30")) {
                fraction += diffs.months / 12;
                start.add(diffs.months, 'months');
                diffs.days = dayDiff30_360_Bond(start, end);
            }
            fraction += dayFraction(start, end, dayCount, diffs.days);
            // Regardless of the annualized rate, remaining days should calculate from the regular interest rate
            rate = effectivePeriodInterest(interestrate, 'yearly', dayCount);
            amount = interestAdded(amount, startAmount, rate, compoundingFrequency, 1, fraction);
        }
    // Monthly, Quarterly
    } else if (frequency >= 1) {
        if (diffs.months >= frequency) {
            let periods = Math.floor(diffs.months / frequency);
            amount = interestAdded(amount, startAmount, rate, compoundingFrequency, periods);
            start.add((periods * frequency), 'months');
            diffs = calcDiffs(start, end);
        }
        if (diffs.days > 0) {
            let fraction = 0;
            // If 30 day count months separately
            if (diffs.months > 0 && dayCount.includes("30")) {
                fraction += diffs.months / 12;
                start = addMonthsMaintainDate(start, diffs.months, startDay);
                diffs.days = dayDiff30_360_Bond(start, end);
            }
            // Final days fraction needs to be taken from the annualize interest rate
            rate = effectivePeriodInterest(interestrate, 'yearly', dayCount);
            fraction += dayFraction(start, end, dayCount, diffs.days);
            amount = interestAdded(amount, startAmount, rate, compoundingFrequency, 1, fraction);
        }
    // Bi-weekly, weekly, daily (if we had it)
    } else {
        // Frequency is a rounded fraction of a month so 4 * 7 to day the days interval
        if (diffs.days > 0 && diffs.days > 28 * frequency) {
            let periods = Math.floor(diffs.days / (28 * frequency));
            amount = interestAdded(amount, startAmount, rate, compoundingFrequency, periods);
            start.add((periods * 28 * frequency), "days");
            diffs = calcDiffs(start, end);
        }
        if (diffs.days > 0) {
            let fraction = dayFraction(start, end, dayCount, diffs.days);
            // Final days fraction needs to be taken from the annualize interest rate
            rate = effectivePeriodInterest(interestrate, 'yearly', dayCount);
            amount = interestAdded(amount, startAmount, rate, compoundingFrequency, 1, fraction);
        }
    }
    return (Math.round(amount * 100) / 100);
}

export function debtinterest(tran:Transaction, convertDate = currentDate()) {
    if (!tran.attrs.interestascash && tran.effective_date && tran.attrs.interestrate && tran.attrs.amount) {
        return interestCalc(tran.attrs.amount, tran.effective_date, convertDate, parseFloat(String(tran.attrs.interestrate)), tran.attrs.interestratefreq, tran.attrs.interest_count);
    } else {
        return tran.attrs.amount;
    }
}

export function conversion(convertTran:any) {
    convertTran.newtran.attrs.method = convertTran.method;
    if (convertTran.method == "Valuation") {
        const discount = !isNaN(parseFloat(convertTran.tran.attrs.discount)) ? (parseFloat(convertTran.tran.attrs.discount) / 100) : 0;
        let regularppshare = parseFloat(convertTran.toissue.ppshare) * (1 - discount);
        if (!isNaN(parseFloat(convertTran.toissue.premoney)) && !isNaN(parseFloat(convertTran.tran.attrs.valcap))) {
            var premoneypercent = (1-(parseFloat(convertTran.tran.attrs.valcap) / parseFloat(convertTran.toissue.premoney)));
            convertTran.newtran.prevalcappercentage = String(premoneypercent * 100);
            if (premoneypercent > (discount)) {
                regularppshare = parseFloat(convertTran.toissue.ppshare) * (1-premoneypercent);
                convertTran.newtran.caphit = true;
            }
        }
        if (!isNaN(parseFloat(convertTran.toissue.ppshare))) {
            convertTran.newtran.attrs.effectivepps = regularppshare;
        }
    }
    else if (convertTran.method == "Price Per Share") {
        convertTran.newtran.attrs.effectivepps = parseFloat(convertTran.ppshare);
    }
    convertTran.newtran.attrs.units = convertTran.newtran.attrs.amount / convertTran.newtran.attrs.effectivepps;
    return convertTran.newtran;
}

export function isNumber(val:string|number) {
    return !isNaN(parseFloat(String(val)));
}

export function primaryMeasure(sec_type:string) {
    switch (sec_type) {
        case "Equity":
        case "Option":
        case "Warrant":
            return "units";
        case "Debt":
        case "Convertible Debt":
            return "amount";
    }
    return null;
}

export function cleannumber(potentialnumber: string) {
    if (potentialnumber) {
        let finalnumber = String(potentialnumber).replace(/\,/g,'');
        finalnumber = String(finalnumber).replace(/\$/g , '');
        return finalnumber.trim();
    }
}

export function parseFormattedNumber(num: string|number) {
    return parseFloat(String(num).replace(/[^0-9.]/g,''));
}

export function cleandatestr(potentialdate:string, format:string):Date|string {
    if (potentialdate) {
        let splitdate:Array<string>;
        if (potentialdate.indexOf('-') != -1) {
            splitdate = potentialdate.trim().split("-");
        } else {
            splitdate = potentialdate.trim().split("/");
        }
        if (splitdate.length != 3 || (!isNumber(splitdate[0]) && !isNumber(splitdate[1]) && !isNumber(splitdate[2]))) {
            return '';
        } else {
            if (splitdate[2].length == 2) {
                splitdate[2] = "20" + splitdate[2];
            }
            let splitdatenums = splitdate.map(datepart => parseFloat(datepart));
            if (format == 'MM/dd/yyyy') {
                return newUTCDate(splitdatenums[2], (splitdatenums[0] - 1), splitdatenums[1]);
            } else {
                return newUTCDate(splitdatenums[2], (splitdatenums[1] - 1), (splitdatenums[0]));
            }
        }
    }
}

export function hashCode(str_input:string) {
    var hash = 0, i:number, chr:number, len:number;
    if (str_input.length === 0) {
        return hash;
    }
    for (i = 0, len = str_input.length; i < len; i++) {
        chr   = str_input.charCodeAt(i);
        hash  = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export function sum_ledger(entries:Array<LedgerEntry>, captable:CapTable, options:Option_type = {}) {
    let {as_converted = false} = options;
    if (as_converted) {
        return entries.reduce(
            function(prev, cur) {
                return prev + (cur.units * captable.tranForId(cur.transaction).conversionRatio(captable));
            }, 0);
    } else {
        return entries.reduce(
            function(prev, cur) {
                return prev + cur.units;
            }, 0);
    }
}

export function sumObjectVals(a:any, b:any) {
    if (a === undefined) {
        a = {};
    }
    for (let val in b) {
        if (b.hasOwnProperty(val)) {
            if (!a[val]) {
                a[val] = 0;
            }
            a[val] += b[val];
        }
    }
    return a;
}



// WEBPACK FOOTER //
// ./_src/services/constants/calculate.ts